import { writeDisplayComment, writeDisplayReaction } from './cacheStore';
import { getOpenUserStore, openUserStoreFromSession, type CachedComment, type CachedReaction, type UserStore } from './store';
import type { Comment, Reaction, VideoMediaEntry } from './types';

/**
 * Comment and reaction cache used by the UI. Rows live in the open user store.
 * Server mirrors go through cacheStore; these helpers also keep local pending flags.
 */

async function storeOrNull(): Promise<UserStore | null> {
  const open = getOpenUserStore();
  if (open) {
    return open;
  }
  return openUserStoreFromSession();
}

function recordOf(value: object): Record<string, unknown> {
  const record: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    if (item !== undefined) {
      record[key] = item;
    }
  }
  return record;
}

function toComment(row: CachedComment): Comment {
  const comment: Comment = {
    id: row.id,
    incident_id: row.incident_id,
    user_id: typeof row.user_id === 'string' ? row.user_id : '',
    comment: typeof row.comment === 'string' ? row.comment : '',
    created_at: typeof row.created_at === 'string' ? row.created_at : '',
  };
  if (typeof row.username === 'string') comment.username = row.username;
  if (typeof row.profileImageUrl === 'string') comment.profileImageUrl = row.profileImageUrl;
  if (typeof row.updated_at === 'string') comment.updated_at = row.updated_at;
  if (row.deleted === true) comment.deleted = true;
  if (typeof row.last_synced === 'string') comment.last_synced = row.last_synced;
  if (row.local_only === true) comment.local_only = true;
  if (Array.isArray(row.image_data)) {
    comment.image_data = row.image_data.filter((item): item is string => typeof item === 'string');
  }
  if (typeof row.image_count === 'number') comment.image_count = row.image_count;
  if (row.is_resolution === true) comment.is_resolution = true;
  if (Array.isArray(row.media)) {
    const media = row.media.filter((item): item is VideoMediaEntry => {
      if (!item || typeof item !== 'object') {
        return false;
      }
      const photoId = 'photoId' in item ? item.photoId : undefined;
      return typeof photoId === 'number';
    });
    if (media.length > 0) {
      comment.media = media;
    }
  }
  if (row.can_edit_resolution === true) comment.can_edit_resolution = true;
  if (row.sync_action === 'create' || row.sync_action === 'update') comment.sync_action = row.sync_action;
  if (Array.isArray(row.pending_added_images)) {
    comment.pending_added_images = row.pending_added_images.filter((item): item is string => typeof item === 'string');
  }
  if (Array.isArray(row.pending_deleted_image_indexes)) {
    comment.pending_deleted_image_indexes = row.pending_deleted_image_indexes.filter(
      (item): item is number => typeof item === 'number'
    );
  }
  return comment;
}

function toReaction(row: CachedReaction): Reaction {
  const reaction: Reaction = {
    id: row.id,
    user_id: typeof row.user_id === 'string' ? row.user_id : '',
    reaction_type_name: typeof row.reaction_type_name === 'string' ? row.reaction_type_name : '',
    content_type: row.content_type === 'comment' ? 'comment' : 'card',
    content_id: row.content_id,
    created_at: typeof row.created_at === 'string' ? row.created_at : '',
  };
  if (typeof row.reaction_type_id === 'number') reaction.reaction_type_id = row.reaction_type_id;
  if (row.deleted === true) reaction.deleted = true;
  if (typeof row.last_synced === 'string') reaction.last_synced = row.last_synced;
  if (row.local_only === true) reaction.local_only = true;
  return reaction;
}

class RowQuery<T extends { id?: string }> {
  constructor(
    private readonly load: () => Promise<T[]>,
    private readonly pred: (row: T) => boolean = () => true,
    private readonly reversed = false,
    private readonly remove?: (id: string) => Promise<void>
  ) {}

  filter(pred: (row: T) => boolean): RowQuery<T> {
    return new RowQuery(this.load, (row) => this.pred(row) && pred(row), this.reversed, this.remove);
  }

  reverse(): RowQuery<T> {
    return new RowQuery(this.load, this.pred, !this.reversed, this.remove);
  }

  private async matched(): Promise<T[]> {
    const rows = (await this.load()).filter(this.pred);
    return this.reversed ? rows.reverse() : rows;
  }

  async toArray(): Promise<T[]> {
    return this.matched();
  }

  async first(): Promise<T | undefined> {
    const rows = await this.matched();
    return rows[0];
  }

  async count(): Promise<number> {
    const rows = await this.matched();
    return rows.length;
  }

  async sortBy(field: keyof T): Promise<T[]> {
    const rows = (await this.load()).filter(this.pred);
    rows.sort((left, right) => String(left[field] ?? '').localeCompare(String(right[field] ?? '')));
    return this.reversed ? rows.reverse() : rows;
  }

  async delete(): Promise<void> {
    if (!this.remove) {
      return;
    }
    const rows = await this.matched();
    for (const row of rows) {
      if (row.id) {
        await this.remove(row.id);
      }
    }
  }
}

function constraintError(): Error {
  const error = new Error('Key already exists in the object store');
  error.name = 'ConstraintError';
  return error;
}

export const commentTable = {
  async get(id: string): Promise<Comment | undefined> {
    const store = await storeOrNull();
    if (!store) {
      return undefined;
    }
    const row = await store.comments.get(id);
    return row ? toComment(row) : undefined;
  },

  async add(row: Comment): Promise<string> {
    const store = await storeOrNull();
    if (!store || !row.id) {
      throw new Error('User store is not open');
    }
    const existing = await store.comments.get(row.id);
    if (existing) {
      throw constraintError();
    }
    await writeDisplayComment(store, recordOf(row));
    return row.id;
  },

  async put(row: Comment): Promise<string> {
    const store = await storeOrNull();
    if (!store || !row.id) {
      throw new Error('User store is not open');
    }
    await writeDisplayComment(store, recordOf(row));
    return row.id;
  },

  async update(id: string, patch: Partial<Comment>): Promise<number> {
    const store = await storeOrNull();
    if (!store) {
      return 0;
    }
    const existing = await store.comments.get(id);
    if (!existing) {
      return 0;
    }
    const merged = toComment(existing);
    const next: Comment = { ...merged, ...patch, incident_id: patch.incident_id ?? merged.incident_id };
    if ('last_synced' in patch && patch.last_synced === undefined) {
      delete next.last_synced;
    }
    const nextId = next.id && next.id !== id ? next.id : id;
    next.id = nextId;
    if (nextId !== id) {
      await store.comments.delete(id);
    }
    await writeDisplayComment(store, recordOf(next));
    return 1;
  },

  async delete(id: string): Promise<void> {
    const store = await storeOrNull();
    if (!store) {
      return;
    }
    await store.comments.delete(id);
  },

  where(index: 'incident_id' | 'user_id') {
    return {
      equals(value: string): RowQuery<Comment> {
        return new RowQuery(async () => {
          const store = await storeOrNull();
          if (!store) {
            return [];
          }
          const rows = await store.comments.where(index).equals(value).toArray();
          return rows.map(toComment);
        }, () => true, false, async (id) => {
          const store = await storeOrNull();
          if (store) {
            await store.comments.delete(id);
          }
        });
      },
    };
  },

  filter(pred: (row: Comment) => boolean): RowQuery<Comment> {
    return new RowQuery(async () => {
      const store = await storeOrNull();
      if (!store) {
        return [];
      }
      const rows = await store.comments.toArray();
      return rows.map(toComment);
    }, pred, false, async (id) => {
      const store = await storeOrNull();
      if (store) {
        await store.comments.delete(id);
      }
    });
  },
};

export const reactionTable = {
  async get(id: string): Promise<Reaction | undefined> {
    const store = await storeOrNull();
    if (!store) {
      return undefined;
    }
    const row = await store.reactions.get(id);
    return row ? toReaction(row) : undefined;
  },

  async add(row: Reaction): Promise<string> {
    const store = await storeOrNull();
    if (!store) {
      throw new Error('User store is not open');
    }
    const existing = await store.reactions.get(row.id);
    if (existing) {
      throw constraintError();
    }
    await writeDisplayReaction(store, recordOf(row));
    return row.id;
  },

  async put(row: Reaction): Promise<string> {
    const store = await storeOrNull();
    if (!store) {
      throw new Error('User store is not open');
    }
    await writeDisplayReaction(store, recordOf(row));
    return row.id;
  },

  async update(id: string, patch: Partial<Reaction>): Promise<number> {
    const store = await storeOrNull();
    if (!store) {
      return 0;
    }
    const existing = await store.reactions.get(id);
    if (!existing) {
      return 0;
    }
    const merged = { ...toReaction(existing), ...patch };
    if ('last_synced' in patch && patch.last_synced === undefined) {
      delete merged.last_synced;
    }
    const nextId = patch.id && patch.id !== id ? patch.id : id;
    merged.id = nextId;
    if (nextId !== id) {
      await store.reactions.delete(id);
    }
    await writeDisplayReaction(store, recordOf(merged));
    return 1;
  },

  async delete(id: string): Promise<void> {
    const store = await storeOrNull();
    if (!store) {
      return;
    }
    await store.reactions.delete(id);
  },

  async clear(): Promise<void> {
    const store = await storeOrNull();
    if (!store) {
      return;
    }
    await store.reactions.clear();
  },

  async bulkPut(rows: Reaction[]): Promise<void> {
    for (const row of rows) {
      await this.put(row);
    }
  },

  where(index: string | string[]) {
    return {
      equals(value: string | string[]): RowQuery<Reaction> {
        const fields = Array.isArray(index) ? index : [index];
        const expected = Array.isArray(value) ? value : [value];
        return new RowQuery(async () => {
          const store = await storeOrNull();
          if (!store) {
            return [];
          }
          const contentIndex = fields.indexOf('content_id');
          const contentId = contentIndex >= 0 ? expected[contentIndex] : undefined;
          const rows = contentId
            ? await store.reactions.where('content_id').equals(contentId).toArray()
            : await store.reactions.toArray();
          return rows.map(toReaction).filter((row) => fields.every((field, i) => {
            const current = row[field as keyof Reaction];
            return String(current ?? '') === String(expected[i] ?? '');
          }));
        }, () => true, false, async (id) => {
          const store = await storeOrNull();
          if (store) {
            await store.reactions.delete(id);
          }
        });
      },
    };
  },

  filter(pred: (row: Reaction) => boolean): RowQuery<Reaction> {
    return new RowQuery(async () => {
      const store = await storeOrNull();
      if (!store) {
        return [];
      }
      const rows = await store.reactions.toArray();
      return rows.map(toReaction);
    }, pred, false, async (id) => {
      const store = await storeOrNull();
      if (store) {
        await store.reactions.delete(id);
      }
    });
  },
};

export async function readSyncMeta(key: string): Promise<string | undefined> {
  const store = await storeOrNull();
  if (!store) {
    return undefined;
  }
  const row = await store.meta.get(key);
  return row?.value;
}

export async function writeSyncMeta(key: string, value: string): Promise<void> {
  const store = await storeOrNull();
  if (!store) {
    return;
  }
  await store.meta.put({ key, value });
}

export async function writeDisplayCommentRow(row: Record<string, unknown>): Promise<void> {
  const store = await storeOrNull();
  if (!store) {
    return;
  }
  await writeDisplayComment(store, row);
}

export async function writeDisplayReactionRow(row: Record<string, unknown>): Promise<void> {
  const store = await storeOrNull();
  if (!store) {
    return;
  }
  await writeDisplayReaction(store, row);
}
